import { useState, useEffect, useRef, useCallback } from "react";
import Layout from "../components/Layout.jsx";
import { documents as docsApi, getAccount, getTenantClient, storage as storageApi, config as configApi } from "../lib/supabase.js";
import * as pdfjsLib from "pdfjs-dist";
import DocumentWorkstation from "../components/DocumentWorkstation.jsx";
import { useUserRole, hasPermission } from "../App.jsx";

pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;

// ─── Font loader ─────────────────────────────────────────────────────────────

function useFonts() {
  useEffect(() => {
    [
      { id: "gfont-instrument-serif", href: "https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&display=swap" },
      { id: "gfont-dm-sans", href: "https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;1,9..40,400&display=swap" },
    ].forEach((f) => {
      if (!document.getElementById(f.id)) {
        const link = document.createElement("link");
        link.id = f.id; link.rel = "stylesheet"; link.href = f.href;
        document.head.appendChild(link);
      }
    });
  }, []);
}

// ─── Theme tokens ────────────────────────────────────────────────────────────

const L = {
  bg: "#fafaf8", text: "#1a1a1a", textSoft: "#444444", muted: "#8a8a8a",
  dim: "#c0bdb8", border: "#e5e3de", borderLight: "#f0eee9",
  surface: "#ffffff", surfaceHover: "#f6f5f2", inputBg: "#f6f5f2",
};

const sans = "'DM Sans', -apple-system, sans-serif";
const serif = "'Instrument Serif', Georgia, serif";

const STATUS_STYLE = {
  indexed:    { color: "#15803d", bg: "#f0fdf4", border: "#bbf7d0", label: "Indexed" },
  processing: { color: "#b45309", bg: "#fffbeb", border: "#fde68a", label: "Processing" },
  failed:     { color: "#dc2626", bg: "#fef2f2", border: "#fecaca", label: "Failed" },
  pending:    { color: "#6d28d9", bg: "#f5f3ff", border: "#ddd6fe", label: "Pending" },
};

const inputStyle = {
  width: "100%", padding: "9px 14px", background: L.inputBg,
  border: `1px solid ${L.border}`, borderRadius: 8, color: L.text,
  fontSize: 13, outline: "none", fontFamily: sans, boxSizing: "border-box",
};

const btnGhost = {
  padding: "8px 16px", background: "none", border: `1px solid ${L.border}`,
  borderRadius: 8, color: L.muted, fontSize: 12, fontWeight: 500,
  cursor: "pointer", fontFamily: sans, transition: "color 0.15s, border-color 0.15s",
  whiteSpace: "nowrap",
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

async function extractTextFromFile(file) {
  if (file.name.split(".").pop()?.toLowerCase() === "pdf") {
    const ab = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: ab }).promise;
    let text = "";
    const pageBoundaries = [];
    for (let i = 1; i <= pdf.numPages; i++) {
      const start = text.length;
      const p = await pdf.getPage(i);
      const tc = await p.getTextContent();
      text += tc.items.map((it) => it.str).join(" ") + "\n\n";
      pageBoundaries.push({ page: i, start, end: text.length });
    }
    return { text: text.trim(), pages: pdf.numPages, pageBoundaries };
  }
  return { text: await file.text(), pages: 1, pageBoundaries: [{ page: 1, start: 0, end: (await file.text()).length }] };
}

const UPLOAD_STATES = { IDLE: "idle", EXTRACTING: "extracting", PROCESSING: "processing", COMPLETE: "complete", ERROR: "error" };
const platformUrl = import.meta.env.VITE_SUPABASE_URL;
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
function edgeFnUrl(fn, slug) { return `${platformUrl}/functions/v1/${fn}?account=${slug}`; }

// ─── Upload Modal (light) ────────────────────────────────────────────────────

function UploadModal({ open, onClose, onComplete, account }) {
  const [files, setFiles] = useState([]);
  const [category, setCategory] = useState("");
  const [uploadStates, setUploadStates] = useState({});
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);
  const dropRef = useRef(null);
  const uploadUrl = account?.slug ? edgeFnUrl("upload-document", account.slug) : null;

  const handleFiles = useCallback((nf) => { setFiles((p) => [...p, ...Array.from(nf).filter((f) => ["pdf", "txt", "csv", "html"].includes(f.name.split(".").pop()?.toLowerCase()))]); }, []);
  const handleDrop = useCallback((e) => { e.preventDefault(); e.stopPropagation(); if (e.dataTransfer.files?.length) handleFiles(e.dataTransfer.files); }, [handleFiles]);
  const removeFile = (i) => { setFiles((p) => p.filter((_, j) => j !== i)); setUploadStates((p) => { const n = { ...p }; delete n[i]; return n; }); };

  const uploadFile = async (file, idx) => {
    if (!uploadUrl) return;
    try {
      setUploadStates((p) => ({ ...p, [idx]: { state: UPLOAD_STATES.EXTRACTING, progress: 15 } }));
      const { text, pages, pageBoundaries } = await extractTextFromFile(file);
      if (!text.trim()) throw new Error("No text extracted.");
      setUploadStates((p) => ({ ...p, [idx]: { state: UPLOAD_STATES.PROCESSING, progress: 40 } }));
      const res = await fetch(uploadUrl, { method: "POST", headers: { Authorization: `Bearer ${anonKey}`, "Content-Type": "application/json" }, body: JSON.stringify({ filename: file.name, file_type: file.name.split(".").pop()?.toLowerCase() || "pdf", file_size_bytes: file.size, raw_text: text, page_count: pages, page_boundaries: pageBoundaries, category: category || null }) });
      const result = await res.json();
      if (!res.ok || result.error) throw new Error(result.error || "Failed");
      if (result.storage_path) {
        setUploadStates((p) => ({ ...p, [idx]: { state: UPLOAD_STATES.PROCESSING, progress: 80 } }));
        try {
          // Step 5: upload via a proxy-signed URL — no data-plane keys in the browser.
          const fileBuffer = await file.arrayBuffer();
          const { error: upErr } = await storageApi.upload(result.storage_path, fileBuffer, file.type || "application/pdf");
          if (upErr) console.warn("File storage upload failed (non-critical):", upErr);
        } catch (storageErr) { console.warn("File storage upload failed (non-critical):", storageErr); }
      }
      setUploadStates((p) => ({ ...p, [idx]: { state: UPLOAD_STATES.COMPLETE, progress: 100, result } }));
    } catch (err) { setUploadStates((p) => ({ ...p, [idx]: { state: UPLOAD_STATES.ERROR, error: err.message } })); }
  };

  const uploadAll = async () => { setIsUploading(true); for (let i = 0; i < files.length; i++) { if (uploadStates[i]?.state === UPLOAD_STATES.COMPLETE) continue; await uploadFile(files[i], i); } setIsUploading(false); setTimeout(() => onComplete(), 1200); };
  const fmt = (b) => b < 1024 ? `${b} B` : b < 1048576 ? `${(b / 1024).toFixed(1)} KB` : `${(b / 1048576).toFixed(1)} MB`;

  if (!open) return null;
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.25)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, backdropFilter: "blur(6px)" }} onClick={onClose}>
      <div style={{ background: L.surface, border: `1px solid ${L.border}`, borderRadius: 16, padding: 32, width: 540, maxHeight: "80vh", overflowY: "auto", fontFamily: sans, color: L.text, boxShadow: "0 24px 48px rgba(0,0,0,0.08)" }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 400, fontFamily: serif, fontStyle: "italic" }}>Upload documents</h2>
          <button onClick={onClose} style={{ background: "none", border: "none", color: L.dim, fontSize: 20, cursor: "pointer" }}>×</button>
        </div>

        {/* Drop zone */}
        <div ref={dropRef} onDrop={handleDrop} onDragOver={(e) => e.preventDefault()} onClick={() => fileInputRef.current?.click()} style={{ border: `2px dashed ${L.border}`, borderRadius: 12, padding: "36px 20px", textAlign: "center", cursor: "pointer", marginBottom: 20, transition: "border-color 0.15s" }} onMouseEnter={(e) => { e.currentTarget.style.borderColor = L.text; }} onMouseLeave={(e) => { e.currentTarget.style.borderColor = L.border; }}>
          <div style={{ fontSize: 14, color: L.muted }}>Drop files or <span style={{ color: L.text, fontWeight: 600, textDecoration: "underline", textUnderlineOffset: 3 }}>browse</span></div>
          <div style={{ fontSize: 11, color: L.dim, marginTop: 6 }}>PDF, TXT, CSV, HTML</div>
        </div>
        <input ref={fileInputRef} type="file" multiple accept=".pdf,.txt,.csv,.html" style={{ display: "none" }} onChange={(e) => { if (e.target.files?.length) handleFiles(e.target.files); e.target.value = ""; }} />

        {/* Category */}
        <div style={{ marginBottom: 20 }}>
          <label style={{ display: "block", fontSize: 10, color: L.muted, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 6, fontWeight: 600 }}>Category</label>
          <input value={category} onChange={(e) => setCategory(e.target.value)} placeholder="e.g., Installation Guide..." style={inputStyle} />
        </div>

        {/* File list */}
        {files.length > 0 && (
          <div style={{ marginBottom: 20 }}>
            {files.map((file, i) => {
              const st = uploadStates[i];
              const stColor = st?.state === UPLOAD_STATES.COMPLETE ? "#15803d" : st?.state === UPLOAD_STATES.ERROR ? "#dc2626" : st?.state === UPLOAD_STATES.PROCESSING ? "#b45309" : "#1d4ed8";
              return (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 14px", background: L.inputBg, border: `1px solid ${L.borderLight}`, borderRadius: 10, marginBottom: 6 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{file.name}</div>
                    <div style={{ fontSize: 11, color: L.muted }}>
                      {fmt(file.size)}
                      {st?.state === UPLOAD_STATES.COMPLETE && <span style={{ color: "#15803d", marginLeft: 8 }}>✓ {st.result?.chunk_count} chunks</span>}
                      {st?.state === UPLOAD_STATES.ERROR && <span style={{ color: "#dc2626", marginLeft: 8 }}>✗ {st.error}</span>}
                      {st?.state === UPLOAD_STATES.EXTRACTING && <span style={{ color: "#1d4ed8", marginLeft: 8 }}>Extracting...</span>}
                      {st?.state === UPLOAD_STATES.PROCESSING && <span style={{ color: "#b45309", marginLeft: 8 }}>Embedding...</span>}
                    </div>
                    {st && (
                      <div style={{ height: 2, background: L.borderLight, borderRadius: 1, marginTop: 6 }}>
                        <div style={{ height: "100%", background: stColor, width: `${st.progress || 0}%`, borderRadius: 1, transition: "width 0.3s" }} />
                      </div>
                    )}
                  </div>
                  {!isUploading && st?.state !== UPLOAD_STATES.COMPLETE && (
                    <button onClick={() => removeFile(i)} style={{ background: "none", border: "none", color: L.dim, cursor: "pointer", fontSize: 16 }}>×</button>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Actions */}
        <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
          <button onClick={onClose} disabled={isUploading} style={{ ...btnGhost, opacity: isUploading ? 0.5 : 1 }}>Cancel</button>
          <button onClick={uploadAll} disabled={!files.length || isUploading} style={{ padding: "9px 24px", background: L.text, border: "none", borderRadius: 8, color: L.bg, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: sans, opacity: (!files.length || isUploading) ? 0.3 : 1 }}>
            {isUploading ? "Processing..." : `Upload ${files.length}`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Documents Page ──────────────────────────────────────────────────────────

export default function Documents() {
  useFonts();
  const account = getAccount();
  const { role } = useUserRole();
  const canEdit = hasPermission(role, "editContent");
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [statusFilter, setStatusFilter] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [deleting, setDeleting] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const searchTimerRef = useRef(null);

  const [wsOpen, setWsOpen] = useState(false);
  const [wsScope, setWsScope] = useState(null);
  const [wsScopeLabel, setWsScopeLabel] = useState("");

  // Editorial state
  const [embeddingConfig, setEmbeddingConfig] = useState(null);
  const [coachDismissed, setCoachDismissed] = useState(false);

  const load = async () => { const { data } = await docsApi.list(); setDocs(data || []); setLoading(false); };
  useEffect(() => { load(); }, []);
  useEffect(() => { if (!docs.some((d) => d.status === "processing")) return; const iv = setInterval(load, 5000); return () => clearInterval(iv); }, [docs]);

  // Load embedding config
  useEffect(() => { configApi.get().then(({ data }) => { if (data) setEmbeddingConfig(data); }); }, []);

  const performSearch = async (q) => {
    if (!q.trim() || !account?.slug) { setSearchResults(null); return; }
    setSearching(true);
    try { const res = await fetch(edgeFnUrl("search-documents-grouped", account.slug), { method: "POST", headers: { Authorization: `Bearer ${anonKey}`, "Content-Type": "application/json" }, body: JSON.stringify({ query: q, limit: 20 }) }); const data = await res.json(); setSearchResults(data.results || []); } catch { setSearchResults([]); }
    setSearching(false);
  };
  const handleSearchChange = (v) => { setSearchInput(v); if (searchTimerRef.current) clearTimeout(searchTimerRef.current); if (v.trim().length < 3) { setSearchResults(null); return; } searchTimerRef.current = setTimeout(() => performSearch(v), 500); };
  const clearSearch = () => { setSearchInput(""); setSearchResults(null); };

  const toggleSelect = (id) => { setSelected((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; }); };
  const toggleSelectAll = () => { const vis = displayDocs.map((d) => d.id); const all = vis.every((id) => selected.has(id)); setSelected((p) => { const n = new Set(p); vis.forEach((id) => all ? n.delete(id) : n.add(id)); return n; }); };

  const handleDeleteSingle = async (id) => { if (!confirm("Delete this document and all its chunks?")) return; await docsApi.delete(id); setSelected((p) => { const n = new Set(p); n.delete(id); return n; }); load(); };
  const handleDeleteSelected = async () => { if (!selected.size || !confirm(`Delete ${selected.size} document${selected.size > 1 ? "s" : ""}?`)) return; setDeleting(true); for (const id of selected) await docsApi.delete(id); setSelected(new Set()); setDeleting(false); load(); };

  const openWorkstation = (scope, label) => { setWsScope(scope); setWsScopeLabel(label); setWsOpen(true); };

  const isSearchActive = searchResults !== null;
  const displayDocs = isSearchActive
    ? searchResults.map((sr) => { const doc = docs.find((d) => d.id === sr.document_id); return doc ? { ...doc, _searchScore: sr.score, _snippet: sr.top_snippet, _section: sr.top_section, _matchingChunks: sr.matching_chunks } : null; }).filter(Boolean)
    : docs.filter((d) => !statusFilter || d.status === statusFilter);

  const stats = {
    total: docs.length,
    indexed: docs.filter((d) => d.status === "indexed").length,
    processing: docs.filter((d) => d.status === "processing").length,
    failed: docs.filter((d) => d.status === "failed").length,
    totalChunks: docs.reduce((s, d) => s + (d.chunk_count || 0), 0),
  };
  const coveragePct = stats.total > 0 ? Math.round((stats.indexed / stats.total) * 100) : 0;

  // Knowledge Base brief
  const briefParts = [];
  if (stats.total > 0) {
    briefParts.push(`Your knowledge base contains ${stats.total} document${stats.total !== 1 ? "s" : ""} with ${stats.totalChunks} indexed chunks.`);
    if (stats.indexed > 0) briefParts.push("The AI uses these to answer customer questions — when a visitor asks a question, the system finds the most relevant chunks and passes them to the AI as context.");
    if (stats.processing > 0) briefParts.push(`${stats.processing} document${stats.processing !== 1 ? "s are" : " is"} still processing.`);
    if (stats.failed > 0) briefParts.push(`${stats.failed} failed to process — try re-uploading.`);
  }
  const briefText = briefParts.length > 0 ? briefParts.join(" ") : null;

  // Stale embedding detection
  const embProvider = embeddingConfig?.embedding_provider || "openai";
  const embModel = embeddingConfig?.embedding_model || "text-embedding-3-small";
  const lastEmbeddedAt = embeddingConfig?.last_embedded_at;
  const isStale = embeddingConfig && lastEmbeddedAt && (
    (embeddingConfig._prev_provider && embeddingConfig._prev_provider !== embProvider) ||
    (embeddingConfig._prev_model && embeddingConfig._prev_model !== embModel)
  );
  // Simpler stale check: if config has changed since last embed, the edge function sets a flag
  const needsReEmbed = embeddingConfig?.embedding_status === "needs_reembed";

  return (
    <Layout>
      <style>{`
        @keyframes ov-up { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>

      <div style={{
        minHeight: "calc(100vh - 56px)", background: L.bg,
        margin: -28, padding: "40px 44px", fontFamily: sans, color: L.text,
      }}>
        {/* ── Header ── */}
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "flex-start",
          marginBottom: 32, animation: "ov-up 0.5s ease both",
        }}>
          <div>
            <h1 style={{ fontSize: 36, fontWeight: 400, lineHeight: 1.1, margin: "0 0 8px", fontFamily: serif, fontStyle: "italic" }}>
              Documents
            </h1>
            <p style={{ margin: 0, fontSize: 13, color: L.muted }}>Manage your knowledge base</p>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={() => openWorkstation(null, "All Documents")} style={btnGhost}>Ask AI</button>
            {canEdit && (
              <button onClick={() => setShowUpload(true)} style={{
                padding: "8px 20px", background: L.text, border: "none", borderRadius: 8,
                color: L.bg, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: sans,
              }}>
                Upload
              </button>
            )}
          </div>
        </div>

        {/* ── Knowledge Base Brief ── */}
        {briefText && (
          <div style={{ padding: "20px 0 18px", borderBottom: `1px solid ${L.border}`, marginBottom: 22, animation: "ov-up 0.5s ease 0.03s both" }}>
            <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase", color: L.muted, marginBottom: 12 }}>The brief</div>
            <div style={{ fontSize: 14, lineHeight: 1.75, color: L.textSoft, fontFamily: sans, maxWidth: 680 }}>{briefText}</div>
          </div>
        )}

        {/* ── Coaching card (first-time, dismissible) ── */}
        {!coachDismissed && stats.total <= 3 && stats.total > 0 && (
          <div style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "16px 20px", background: "#f5f3ff", border: "1px solid #ddd6fe", borderRadius: 12, marginBottom: 18, animation: "ov-up 0.3s ease both" }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: "#6d28d9", marginBottom: 6 }}>How your AI reads documents</div>
              <div style={{ fontSize: 13, color: "#444444", lineHeight: 1.7 }}>
                When you upload a document, three things happen: the text is extracted from the file, then split into small overlapping chunks (like paragraphs), and finally each chunk is converted into a mathematical representation that lets the AI quickly find the most relevant passages when a visitor asks a question. The more specific and well-structured your documents are, the better the AI performs.
              </div>
            </div>
            <button onClick={() => setCoachDismissed(true)} style={{ background: "none", border: "none", color: "#6d28d9", cursor: "pointer", fontSize: 16, flexShrink: 0, opacity: 0.5 }}>×</button>
          </div>
        )}

        {/* ── AI Engine info panel ── */}
        {embeddingConfig && stats.total > 0 && (
          <div style={{ display: "flex", alignItems: "center", gap: 16, padding: "14px 20px", background: L.surface, border: `1px solid ${L.border}`, borderRadius: 10, marginBottom: 18, animation: "ov-up 0.5s ease 0.04s both", flexWrap: "wrap" }}>
            <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: L.muted }}>Embedding engine</div>
            <div style={{ fontSize: 13, color: L.textSoft }}>
              {embProvider.charAt(0).toUpperCase() + embProvider.slice(1)} / <span style={{ fontFamily: "monospace", fontSize: 12 }}>{embModel}</span> / 1024 dimensions
            </div>
            {lastEmbeddedAt && <div style={{ fontSize: 11, color: L.dim }}>Last processed {new Date(lastEmbeddedAt).toLocaleDateString()}</div>}
            {coveragePct === 100 && stats.total > 0 && <span style={{ fontSize: 10, color: "#15803d", fontWeight: 600 }}>✓ All indexed</span>}
            {coveragePct < 100 && stats.total > 0 && coveragePct > 0 && <span style={{ fontSize: 10, color: "#b45309", fontWeight: 600 }}>{coveragePct}% indexed</span>}
            <div style={{ flex: 1 }} />
            <a href="/settings" style={{ fontSize: 12, color: L.muted, textDecoration: "underline", textUnderlineOffset: 3 }}>Change in Settings →</a>
          </div>
        )}

        {/* ── Stale embedding warning ── */}
        {needsReEmbed && (
          <div style={{ padding: "12px 18px", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 10, marginBottom: 18, animation: "ov-up 0.3s ease both" }}>
            <div style={{ fontSize: 13, color: "#b45309", lineHeight: 1.65 }}>
              Your embedding model has changed since these documents were last processed. Documents indexed with the old model may return less accurate results. <a href="/settings" style={{ color: "#b45309", fontWeight: 600 }}>Re-embed all documents in Settings →</a>
            </div>
          </div>
        )}

        {/* ── Search ── */}
        <div style={{ marginBottom: 20, position: "relative", animation: "ov-up 0.5s ease 0.05s both" }}>
          <input
            value={searchInput}
            onChange={(e) => handleSearchChange(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && searchInput.trim().length >= 2) { clearTimeout(searchTimerRef.current); performSearch(searchInput); } }}
            placeholder={stats.indexed ? "Semantic search across documents..." : "Search documents..."}
            style={{ ...inputStyle, paddingRight: isSearchActive ? 36 : 14, borderColor: isSearchActive ? "#bfdbfe" : L.border }}
          />
          {searching && <span style={{ position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)", fontSize: 12, color: L.dim, animation: "spin 1s linear infinite", display: "inline-block" }}>⟳</span>}
          {isSearchActive && !searching && <button onClick={clearSearch} style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: L.dim, cursor: "pointer", fontSize: 14 }}>×</button>}
        </div>

        {/* Search indicator */}
        {isSearchActive && (
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16, padding: "8px 14px", background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 8, animation: "ov-up 0.3s ease both" }}>
            <span style={{ fontSize: 12, color: "#1d4ed8", fontWeight: 500 }}>Vector search</span>
            <span style={{ fontSize: 12, color: L.muted }}>{searchResults.length} matched</span>
            <span style={{ fontSize: 12, color: L.textSoft }}>"{searchInput}"</span>
            <span style={{ fontSize: 11, color: L.dim, fontStyle: "italic" }}>Scores show how closely each document matches your query</span>
            <div style={{ flex: 1 }} />
            <button onClick={clearSearch} style={{ fontSize: 12, color: L.muted, background: "none", border: "none", cursor: "pointer", fontFamily: sans, textDecoration: "underline", textUnderlineOffset: 3 }}>Clear</button>
          </div>
        )}

        {/* Bulk actions */}
        {selected.size > 0 && (
          <div style={{ display: "flex", alignItems: "center", gap: 14, padding: "10px 18px", background: L.surface, border: `1px solid ${L.border}`, borderRadius: 10, marginBottom: 16, animation: "ov-up 0.3s ease both" }}>
            <span style={{ fontSize: 13, fontWeight: 600 }}>{selected.size} selected</span>
            <div style={{ flex: 1 }} />
            <button onClick={() => openWorkstation([...selected], `${selected.size} Selected`)} style={{ ...btnGhost, padding: "6px 12px", fontSize: 11 }}>Ask selected</button>
            {canEdit && <button onClick={handleDeleteSelected} disabled={deleting} style={{ ...btnGhost, padding: "6px 12px", fontSize: 11, color: "#dc2626", borderColor: "#fecaca", cursor: deleting ? "not-allowed" : "pointer", opacity: deleting ? 0.5 : 1 }}>{deleting ? "Deleting..." : "Delete"}</button>}
            <button onClick={() => setSelected(new Set())} style={{ ...btnGhost, padding: "6px 12px", fontSize: 11 }}>Deselect</button>
          </div>
        )}

        {/* ── Stats row ── */}
        {!isSearchActive && (
          <div style={{
            display: "flex", gap: 0, marginBottom: 20,
            borderTop: `1px solid ${L.border}`, borderBottom: `1px solid ${L.border}`,
            animation: "ov-up 0.5s ease 0.08s both",
          }}>
            {[
              { label: "Total", value: stats.total, filter: null },
              { label: "Indexed", value: stats.indexed, filter: "indexed", color: "#15803d" },
              { label: "Processing", value: stats.processing, filter: "processing", color: "#b45309" },
              { label: "Failed", value: stats.failed, filter: "failed", color: "#dc2626" },
              { label: "Chunks", value: stats.totalChunks, filter: undefined },
              { label: "Coverage", value: coveragePct === 100 && stats.total > 0 ? "100%" : stats.total === 0 ? "—" : `${coveragePct}%`, filter: undefined, color: coveragePct === 100 && stats.total > 0 ? "#15803d" : coveragePct > 0 ? "#b45309" : undefined },
            ].map((s, i) => {
              const active = statusFilter === s.filter && s.filter !== undefined;
              return (
                <div
                  key={s.label}
                  onClick={() => s.filter !== undefined ? setStatusFilter(statusFilter === s.filter ? null : s.filter) : null}
                  style={{
                    padding: "14px 20px", flex: 1,
                    borderRight: i < 5 ? `1px solid ${L.border}` : "none",
                    cursor: s.filter !== undefined ? "pointer" : "default",
                    background: active ? L.surfaceHover : "transparent",
                    transition: "background 0.15s",
                  }}
                >
                  <div style={{ fontSize: 9, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.1em", color: L.muted, marginBottom: 4 }}>{s.label}</div>
                  <div style={{ fontSize: 22, fontWeight: 300, letterSpacing: "-0.02em", color: s.color || L.text }}>{s.value}</div>
                </div>
              );
            })}
          </div>
        )}

        {/* ── Document table ── */}
        <div style={{
          background: L.surface, border: `1px solid ${L.border}`, borderRadius: 12,
          overflow: "hidden", animation: "ov-up 0.5s ease 0.12s both",
        }}>
          {/* Header row */}
          <div style={{
            display: "grid",
            gridTemplateColumns: "36px 2fr 72px 72px 90px 60px 40px",
            padding: "12px 20px", borderBottom: `1px solid ${L.border}`,
            fontSize: 10, color: L.muted, textTransform: "uppercase",
            letterSpacing: "0.1em", fontWeight: 600, alignItems: "center",
          }}>
            <span><input type="checkbox" checked={displayDocs.length > 0 && displayDocs.every((d) => selected.has(d.id))} onChange={toggleSelectAll} style={{ cursor: "pointer" }} /></span>
            <span>{isSearchActive ? "Document (by relevance)" : "Document"}</span>
            <span>Pages</span>
            <span>Chunks</span>
            <span>Status</span>
            <span>Open</span>
            <span></span>
          </div>

          {/* Rows */}
          {loading ? (
            <div style={{ padding: "40px 20px", color: L.muted, fontSize: 13, textAlign: "center" }}>Loading...</div>
          ) : displayDocs.length === 0 ? (
            <div style={{ padding: "56px 20px", color: L.muted, fontSize: 13, textAlign: "center", lineHeight: 1.7 }}>
              {isSearchActive ? "No documents matched your search." :
                docs.length === 0 ? (
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 8 }}>Your knowledge base is empty</div>
                    <div style={{ fontSize: 13, color: L.muted, lineHeight: 1.7, maxWidth: 360, margin: "0 auto 20px" }}>Upload your first document — a product manual, FAQ, or spec sheet. The AI will use it to answer customer questions accurately.</div>
                    {canEdit && (
                      <button onClick={() => setShowUpload(true)} style={{
                        padding: "9px 20px", background: L.text, border: "none", borderRadius: 8,
                        color: L.bg, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: sans,
                      }}>
                        Upload your first document
                      </button>
                    )}
                  </div>
                ) : "No documents match your filter."}
            </div>
          ) : displayDocs.map((d) => {
            const st = STATUS_STYLE[d.status] || STATUS_STYLE.pending;
            return (
              <div key={d.id}>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "36px 2fr 72px 72px 90px 60px 40px",
                    padding: "14px 20px",
                    borderBottom: d._snippet ? "none" : `1px solid ${L.borderLight}`,
                    alignItems: "center",
                    background: selected.has(d.id) ? L.surfaceHover : L.surface,
                    transition: "background 0.1s",
                  }}
                  onMouseEnter={(e) => { if (!selected.has(d.id)) e.currentTarget.style.background = L.surfaceHover; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = selected.has(d.id) ? L.surfaceHover : L.surface; }}
                >
                  <span><input type="checkbox" checked={selected.has(d.id)} onChange={() => toggleSelect(d.id)} style={{ cursor: "pointer" }} /></span>
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontSize: 13, fontWeight: 500 }}>{d.original_filename || d.filename}</span>
                      {d._searchScore != null && (
                        <span style={{ fontSize: 10, fontWeight: 600, padding: "1px 6px", background: "#eff6ff", color: "#1d4ed8", borderRadius: 4 }}>
                          {(d._searchScore * 100).toFixed(0)}%
                        </span>
                      )}
                      {d._matchingChunks > 1 && <span style={{ fontSize: 10, color: L.muted }}>{d._matchingChunks} matches</span>}
                    </div>
                    <div style={{ fontSize: 11, color: L.muted, marginTop: 2 }}>{d.file_type} · {d.category || "uncategorized"}</div>
                    {d.status === "failed" && d.processing_error && <div style={{ fontSize: 11, color: "#dc2626", marginTop: 2 }}>Error: {d.processing_error}</div>}
                  </div>
                  <span style={{ fontSize: 13, color: L.textSoft, fontVariantNumeric: "tabular-nums" }}>{d.page_count || "—"}</span>
                  <span style={{ fontSize: 13, color: L.textSoft, fontVariantNumeric: "tabular-nums" }}>{d.chunk_count || 0}</span>
                  <span style={{
                    display: "inline-block", padding: "3px 8px", borderRadius: 20,
                    fontSize: 10, fontWeight: 600, textTransform: "uppercase",
                    background: st.bg, color: st.color, border: `1px solid ${st.border}`,
                    letterSpacing: "0.04em",
                  }}>
                    {d.status === "processing" && <span style={{ display: "inline-block", marginRight: 4, animation: "spin 1s linear infinite" }}>⟳</span>}
                    {st.label}
                  </span>
                  {d.status === "processing" && <div style={{ fontSize: 10, color: "#b45309", marginTop: 3, gridColumn: "5", lineHeight: 1.4 }}>Splitting into chunks and creating vectors...</div>}
                  {d.status === "failed" && !d.processing_error && <div style={{ fontSize: 10, color: "#dc2626", marginTop: 3, gridColumn: "5", lineHeight: 1.4 }}>Try re-uploading this file.</div>}
                  <span>
                    {d.status === "indexed" && (
                      <button
                        onClick={() => openWorkstation([d.id], d.original_filename || d.filename)}
                        title={`Open ${d.original_filename} in workstation`}
                        style={{
                          padding: "4px 10px", background: L.inputBg, border: `1px solid ${L.border}`,
                          borderRadius: 6, color: L.textSoft, fontSize: 11, fontWeight: 500,
                          cursor: "pointer", fontFamily: sans, transition: "color 0.15s",
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.color = L.text; }}
                        onMouseLeave={(e) => { e.currentTarget.style.color = L.textSoft; }}
                      >
                        Open
                      </button>
                    )}
                  </span>
                  {canEdit ? (
                    <button
                      onClick={() => handleDeleteSingle(d.id)}
                      style={{ background: "none", border: "none", color: L.dim, cursor: "pointer", fontSize: 13, transition: "color 0.15s" }}
                      onMouseEnter={(e) => { e.currentTarget.style.color = "#dc2626"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.color = L.dim; }}
                      title="Delete"
                    >
                      ×
                    </button>
                  ) : <span />}
                </div>

                {/* Search snippet */}
                {d._snippet && (
                  <div style={{ padding: "0 20px 14px 56px", borderBottom: `1px solid ${L.borderLight}` }}>
                    <div style={{ padding: "10px 14px", background: L.inputBg, borderRadius: 8, border: `1px solid ${L.borderLight}`, fontSize: 12, color: L.textSoft, lineHeight: 1.65, fontStyle: "italic" }}>
                      {d._section && <div style={{ fontSize: 10, color: L.muted, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>{d._section}</div>}
                      "{d._snippet}"
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Workstation */}
        <DocumentWorkstation open={wsOpen} onClose={() => setWsOpen(false)} documents={docs} scope={wsScope} scopeLabel={wsScopeLabel} account={account} getTenantClient={getTenantClient} />

        {/* Upload modal */}
        {canEdit && <UploadModal open={showUpload} onClose={() => setShowUpload(false)} onComplete={() => { setShowUpload(false); load(); }} account={account} />}
      </div>
    </Layout>
  );
}
